import React, { useMemo, useState } from "react";

const MONTH_KEYS = [
  "jan","feb","mar","apr","may","jun",
  "jul","aug","sep","oct","nov","dec",
];

const MONTH_LABELS = MONTH_KEYS.map((m) => m.toUpperCase());

const ITEMS_PER_PAGE = 4;

function parseVarianceSign(value) {
  if (!value || value === "—") return "neutral";
  const s = String(value).trim();
  if (s.startsWith("+")) return "over";
  if (s.startsWith("-")) return "under";
  return "neutral";
}

function VarianceCell({ value }) {
  if (value == null || value === "" || value === "—") {
    return <span className="text-slate-300">—</span>;
  }

  const sign = parseVarianceSign(value);

  const color =
    sign === "over"
      ? "text-orange-500 font-medium"
      : sign === "under"
      ? "text-green-600 font-semibold"
      : "text-slate-500";

  return <span className={`tabular-nums ${color}`}>{value}</span>;
}

function groupRows(data) {
  const groups = [];
  let current = null;

  data.forEach((row) => {
    const name = row.provider || "";
    if (!current || current.provider !== name) {
      current = { provider: name, isTotal: !!row.is_total, rows: [] };
      groups.push(current);
    }
    current.rows.push(row);
  });

  return groups;
}

const Report20ProviderTable = ({ data = [], closedMonthsCount = 0 }) => {
  const [hoveredProvider, setHoveredProvider] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const groups = useMemo(() => groupRows(data), [data]);
  const closedCount = Number(closedMonthsCount) || 0;

  const totalGroup = groups.find((g) => g.isTotal);
  const normalGroups = groups.filter((g) => !g.isTotal);

  const totalPages = Math.ceil(normalGroups.length / ITEMS_PER_PAGE);

  const paginatedGroups = normalGroups.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const displayGroups = totalGroup
    ? [...paginatedGroups, totalGroup]
    : paginatedGroups;

  if (!data || data.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-6">
        No provider data available
      </div>
    );
  }

  const rowTypeLabel = (type) => {
    if (type === "variance") return <span className="italic">VARIANCE</span>;
    return (type || "").toUpperCase();
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, normalGroups.length);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">

      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-slate-900">
          Provider Budget vs Spend
        </h3>
      </div>

      <div className="max-h-[900px] overflow-auto">
        <table className="min-w-full text-sm border-collapse">

          <thead>
            <tr className="sticky top-0 z-30 bg-slate-100 border-b border-slate-300 text-xs font-semibold tracking-wide text-slate-600">
              <th className="sticky left-0 z-20 bg-slate-100 px-4 py-3 text-left min-w-[140px]">
                Provider
              </th>

              <th className="sticky left-[140px] z-20 bg-slate-100 px-3 py-3 text-left min-w-[88px] border-l border-slate-200">
                Type
              </th>

              {MONTH_LABELS.map((label, i) => (
                <th
                  key={label}
                  className={`px-3 py-3 text-right whitespace-nowrap ${
                    i + 1 > closedCount ? "text-slate-300 italic" : ""
                  }`}
                >
                  {label}
                </th>
              ))}

              <th className="px-4 py-3 text-right bg-slate-50/80 whitespace-nowrap">
                Annual Budget
              </th>
              <th className="px-4 py-3 text-right bg-slate-50/80 whitespace-nowrap">
                YTD Spend
              </th>
              <th className="px-4 py-3 text-right bg-slate-50/80 whitespace-nowrap">
                YTD Budget
              </th>
              <th className="px-4 py-3 text-right bg-slate-50/80 whitespace-nowrap">
                Variance
              </th>
            </tr>
          </thead>

          <tbody>
            {displayGroups.map((group, gi) => {
              const isHovered = hoveredProvider === group.provider;
              const isTotal = group.isTotal;

              // ✅ Improved alternating background
              const blockBg = isTotal
                ? ""
                : gi % 2 === 0
                ? "bg-white"
                : "bg-slate-100";

              const hoverBg = isTotal
                ? ""
                : isHovered
                ? "bg-blue-50"
                : blockBg;

              return group.rows.map((row, ri) => {
                const isFirst = ri === 0;
                const isLast = ri === group.rows.length - 1;
                const isVariance = row.row_type === "variance";

                const rowBg = isTotal
                  ? "bg-slate-900 text-white"
                  : hoverBg;

                return (
                  <tr
                    key={`${group.provider}-${row.row_type}-${ri}`}
                    className={`
                      ${rowBg} transition-colors duration-150
                      ${isFirst ? "border-t-2 border-slate-400 border-l-4 border-slate-500 shadow-sm" : "border-t border-slate-200"}
                      ${isLast ? "border-b-2 border-slate-400" : ""}
                    `}
                    onMouseEnter={() =>
                      !isTotal && setHoveredProvider(group.provider)
                    }
                    onMouseLeave={() => setHoveredProvider(null)}
                  >
                    {isFirst && (
                      <td
                        rowSpan={group.rows.length}
                        className={`sticky left-0 z-10 px-4 py-3 font-semibold align-middle border-r border-slate-200 ${
                          isTotal
                            ? "bg-slate-900 text-white"
                            : "bg-slate-100 text-slate-900"
                        }`}
                      >
                        {group.provider}
                      </td>
                    )}

                    <td
                      className={`sticky left-[139px] z-10 px-3 py-3 text-[11px] font-semibold tracking-wide align-middle border-r border-slate-200 ${
                        isTotal
                          ? "bg-slate-900 text-white"
                          : "bg-slate-50 text-slate-600"
                      } ${isVariance && !isTotal ? "italic" : ""}`}
                    >
                      {rowTypeLabel(row.row_type)}
                    </td>

                    {MONTH_KEYS.map((m) => (
                      <td key={m} className="px-3 py-3 text-right tabular-nums">
                        {isVariance ? (
                          <VarianceCell value={row[m]} />
                        ) : (
                          row[m] ?? <span className="text-slate-300">—</span>
                        )}
                      </td>
                    ))}

                    {["annual_budget","ytd_spend","ytd_budget"].map((key) => (
                      <td key={key} className="px-4 py-3 text-right">
                        {row[key] ?? <span className="text-slate-300">—</span>}
                      </td>
                    ))}

                    <td className="px-4 py-3 text-right">
                      {isVariance ? (
                        <VarianceCell value={row.ytd_variance} />
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination (unchanged) */}
      <div className="flex items-center justify-between px-5 py-3 border-t bg-white text-sm">
        <div className="text-slate-600">
          Showing {startItem} – {endItem} of {normalGroups.length} providers
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 border rounded disabled:opacity-40">«</button>
          <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded disabled:opacity-40">Prev</button>

          {pages.map((p) => (
            <button
              key={p}
              onClick={() => setCurrentPage(p)}
              className={`px-3 py-1 border rounded ${
                currentPage === p ? "bg-blue-600 text-white border-blue-600" : "bg-white"
              }`}
            >
              {p}
            </button>
          ))}

          <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 border rounded disabled:opacity-40">»</button>
        </div>
      </div>
    </div>
  );
};

export default Report20ProviderTable;